import React, { useState, useEffect } from 'react';
import { AlertTriangle, Bell, X, CheckCircle, ChevronRight, Shield, Info, Megaphone } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { getBackendUrl } from '../utils/config';

const API_URL = getBackendUrl();

const PRIORIDADE_CONFIG = {
  alta: { label: 'URGENTE', bg: 'bg-red-500', text: 'text-white', border: 'border-red-500', icon: Shield, headerBg: 'bg-gradient-to-r from-red-600 to-red-500' },
  media: { label: 'Importante', bg: 'bg-amber-500', text: 'text-white', border: 'border-amber-400', icon: Bell, headerBg: 'bg-gradient-to-r from-amber-500 to-orange-500' },
  informativa: { label: 'Info', bg: 'bg-sky-500', text: 'text-white', border: 'border-sky-400', icon: Info, headerBg: 'bg-gradient-to-r from-sky-500 to-blue-500' },
};

const TIPO_CONFIG = {
  geral: { label: 'Geral', icon: Megaphone },
  individual: { label: 'Para si', icon: CheckCircle },
  seccao: { label: 'Secção', icon: Bell },
  automatico: { label: 'Automático', icon: AlertTriangle },
};

const AlertasLoginModal = () => {
  const [alertas, setAlertas] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAlertas();
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const fetchAlertas = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { setLoading(false); return; }

      const response = await axios.get(`${API_URL}/api/alertas/meus/pendentes`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.length > 0) {
        setAlertas(response.data);
        setShow(true);
      }
    } catch (error) {
      console.error('[ALERTAS] Erro ao buscar alertas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarcarLido = async (alertaId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/alertas/${alertaId}/marcar-lido`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (currentIndex < alertas.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        setShow(false);
      }
    } catch (error) {
      console.error('[ALERTAS] Erro ao marcar lido:', error);
    }
  };

  const handleSkipNonCritical = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/alertas/marcar-todos-lidos`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShow(false);
    } catch (error) {
      console.error('[ALERTAS] Erro:', error);
    }
  };

  if (loading || !show || alertas.length === 0) return null;

  const alerta = alertas[currentIndex];
  const config = PRIORIDADE_CONFIG[alerta.prioridade] || PRIORIDADE_CONFIG.media;
  const tipoConfig = TIPO_CONFIG[alerta.tipo] || TIPO_CONFIG.geral;
  const Icon = config.icon;
  const TipoIcon = tipoConfig.icon;
  const isAlta = alerta.prioridade === 'alta';
  const totalPendentes = alertas.length - currentIndex;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] animate-in fade-in duration-300" data-testid="alertas-login-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className={`${config.headerBg} text-white p-5 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2.5 rounded-full">
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${isAlta ? 'bg-white/30' : 'bg-white/20'}`}>
                  {config.label}
                </span>
                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-white/15 flex items-center gap-1">
                  <TipoIcon className="h-3 w-3" />
                  {tipoConfig.label}
                  {alerta.seccoes?.length > 0 && ` - ${alerta.seccoes.join(', ')}`}
                  {!alerta.seccoes?.length && alerta.seccao && ` - ${alerta.seccao}`}
                </span>
              </div>
              <h2 className="text-lg font-semibold mt-1">{alerta.titulo}</h2>
            </div>
          </div>
          {!isAlta && (
            <button onClick={() => setShow(false)} className="text-white/70 hover:text-white p-1">
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-700 text-base leading-relaxed whitespace-pre-line">
            {alerta.mensagem}
          </p>

          {alerta.criado_por_nome && (
            <p className="text-xs text-gray-400 mt-4">
              Enviado por {alerta.criado_por_nome}
              {alerta.recorrencia === 'diario' && ' (diário)'}
              {alerta.recorrencia === 'semanal' && ' (semanal)'}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 pb-5 flex items-center ${totalPendentes > 1 ? 'justify-between' : 'justify-end'}`}>
          {totalPendentes > 1 && (
            <span className="text-xs text-gray-400">
              {currentIndex + 1} de {alertas.length} alertas
            </span>
          )}
          <div className="flex gap-2">
            {!isAlta && totalPendentes > 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSkipNonCritical}
                className="text-gray-500"
                data-testid="skip-all-alerts-btn"
              >
                Ignorar todos
              </Button>
            )}
            <Button
              onClick={() => handleMarcarLido(alerta.id)}
              size="sm"
              className={`${isAlta ? 'bg-red-600 hover:bg-red-700' : ''}`}
              data-testid="mark-alert-read-btn"
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              {totalPendentes > 1 ? 'Li, próximo' : 'Li e entendi'}
              {totalPendentes > 1 && <ChevronRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertasLoginModal;
